import {onboardingProgressToRow} from './onboarding.js';
import {smokingProfileToRow} from './profile.js';
import {smokerCapabilitiesToRow} from './smoker-capabilities.js';
import {SMOKER_CAPABILITY_ENTITY_TYPE} from './smoker-capability-store.js';
import {smokerToRow} from './smoker.js';
import {SMOKER_ENTITY_TYPE} from './smoker-store.js';
import {probeToRow,thermometerToRow} from './thermometer.js';
import {THERMOMETER_ENTITY_TYPE} from './thermometer-store.js';
import {PROBE_ENTITY_TYPE} from './probe-store.js';
import {
  ONBOARDING_PROGRESS_ENTITY_TYPE,
  SMOKING_PROFILE_ENTITY_TYPE
} from './offline.js';
import {woodFavouriteToRow,WOOD_FAVOURITE_ENTITY_TYPE} from './wood-favourites.js';
import {woodInventoryToRow,WOOD_INVENTORY_ENTITY} from './wood-inventory.js';

function requireClient(client){
  if(typeof client?.from!=='function')throw new TypeError('Supabase client is required');
  return client;
}

function asVersion(value){
  const version=Number(value);
  return Number.isInteger(version)&&version>0?version:0;
}

function primaryId(entity,idField){
  const value=String(entity?.[idField]??entity?.id??'').trim();
  if(!value)throw new TypeError('Remote sync entity id is required');
  return value;
}

function publicRow(mapper,entity){
  const row={...mapper(entity)};
  delete row.version;
  return row;
}

function remoteError(error,code='REMOTE_SYNC_FAILED'){
  if(!error)return null;
  const wrapped=new Error(code);
  wrapped.code=String(error.code||code).slice(0,80);
  wrapped.cause=error;
  return wrapped;
}

function createVersionedAdapter(client,{table,idColumn,idField='id',toRow}){
  async function currentVersion(id){
    const {data,error}=await client.from(table)
      .select('version')
      .eq(idColumn,id)
      .maybeSingle();
    if(error)throw remoteError(error,'REMOTE_VERSION_READ_FAILED');
    return data?asVersion(data.version):null;
  }

  async function create(entity){
    const row=publicRow(toRow,entity);
    const {data,error}=await client.from(table)
      .insert(row)
      .select('version')
      .single();
    if(error){
      const version=await currentVersion(primaryId(entity,idField)).catch(()=>null);
      if(version!==null)return Object.freeze({status:'conflict',reason:'already_exists',remoteServerVersion:version});
      throw remoteError(error,'REMOTE_CREATE_FAILED');
    }
    return Object.freeze({ok:true,serverVersion:asVersion(data?.version)||1});
  }

  async function update(entity){
    const id=primaryId(entity,idField);
    const expected=asVersion(entity.serverVersion);
    if(!expected)return Object.freeze({status:'conflict',reason:'missing_local_version',remoteServerVersion:await currentVersion(id)||0});

    const row=publicRow(toRow,entity);
    const {data,error}=await client.from(table)
      .update(row)
      .eq(idColumn,id)
      .eq('version',expected)
      .select('version')
      .maybeSingle();

    if(error)throw remoteError(error,'REMOTE_UPDATE_FAILED');
    if(data)return Object.freeze({ok:true,serverVersion:asVersion(data.version)||expected+1});

    const version=await currentVersion(id);
    if(version===null)return Object.freeze({status:'conflict',reason:'remote_missing',remoteServerVersion:0});
    return Object.freeze({status:'conflict',reason:'version_mismatch',remoteServerVersion:version});
  }

  async function remove(entity){
    const id=primaryId(entity,idField);
    const expected=asVersion(entity.serverVersion);

    if(!expected){
      const version=await currentVersion(id);
      if(version===null)return Object.freeze({ok:true});
      return Object.freeze({
        status:'conflict',
        reason:'missing_local_version',
        remoteServerVersion:version
      });
    }

    const {data,error}=await client.from(table)
      .delete()
      .eq(idColumn,id)
      .eq('version',expected)
      .select(idColumn)
      .maybeSingle();
    if(error)throw remoteError(error,'REMOTE_DELETE_FAILED');
    if(data)return Object.freeze({ok:true});

    const version=await currentVersion(id);
    if(version===null)return Object.freeze({ok:true});
    return Object.freeze({status:'conflict',reason:'version_mismatch',remoteServerVersion:version});
  }

  return Object.freeze({create,update,delete:remove});
}

function createFavouriteAdapter(client){
  async function create(entity){
    const row=woodFavouriteToRow(entity);
    const {error}=await client.from('user_wood_favourites')
      .upsert(row,{onConflict:'user_id,wood_key',ignoreDuplicates:true});
    if(error)throw remoteError(error,'REMOTE_CREATE_FAILED');
    return Object.freeze({ok:true,serverVersion:1});
  }

  async function update(entity){
    return create(entity);
  }

  async function remove(entity){
    const row=woodFavouriteToRow(entity);
    const {error}=await client.from('user_wood_favourites')
      .delete()
      .eq('user_id',row.user_id)
      .eq('wood_key',row.wood_key);
    if(error)throw remoteError(error,'REMOTE_DELETE_FAILED');
    return Object.freeze({ok:true});
  }

  return Object.freeze({create,update,delete:remove});
}

export function createSupabaseSmokehouseRemoteAdapters({client}={}){
  const db=requireClient(client);
  return Object.freeze({
    [SMOKING_PROFILE_ENTITY_TYPE]:createVersionedAdapter(db,{
      table:'smoking_profiles',
      idColumn:'user_id',
      idField:'userId',
      toRow:smokingProfileToRow
    }),
    [ONBOARDING_PROGRESS_ENTITY_TYPE]:createVersionedAdapter(db,{
      table:'onboarding_progress',
      idColumn:'user_id',
      idField:'userId',
      toRow:onboardingProgressToRow
    }),
    [SMOKER_ENTITY_TYPE]:createVersionedAdapter(db,{
      table:'smokers',
      idColumn:'id',
      toRow:smokerToRow
    }),
    [SMOKER_CAPABILITY_ENTITY_TYPE]:createVersionedAdapter(db,{
      table:'smoker_capabilities',
      idColumn:'smoker_id',
      idField:'smokerId',
      toRow:smokerCapabilitiesToRow
    }),
    [THERMOMETER_ENTITY_TYPE]:createVersionedAdapter(db,{
      table:'thermometers',
      idColumn:'id',
      toRow:thermometerToRow
    }),
    [PROBE_ENTITY_TYPE]:createVersionedAdapter(db,{
      table:'thermometer_probes',
      idColumn:'id',
      toRow:probeToRow
    }),
    [WOOD_INVENTORY_ENTITY]:createVersionedAdapter(db,{
      table:'user_wood_inventory',
      idColumn:'id',
      toRow:woodInventoryToRow
    }),
    [WOOD_FAVOURITE_ENTITY_TYPE]:createFavouriteAdapter(db)
  });
}
